import {
  Button as AntButton,
  Card,
  Descriptions,
  Drawer,
  Empty as AntEmpty,
  Form,
  Input,
  Modal,
  Pagination as AntPagination,
  Space,
  Switch,
  Table as AntTable,
  Tag,
  Timeline,
  Typography,
  Upload,
} from 'antd';
import type {
  ButtonProps,
  DescriptionsProps,
  FormInstance,
  PaginationProps,
  SwitchProps,
  TableColumnsType,
  TablePaginationConfig,
  TableProps,
  UploadProps,
} from 'antd';
import {
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  UploadOutlined,
} from '@ant-design/icons';
import type { ReactNode } from 'react';
import { normalizeRiskReason, resolveStatusColor, resolveStatusLabel } from './presentation';

export { adminDarkTheme, adminTheme } from './theme';
export * from './date';
export { AdminDatePicker, AdminDateRangePicker } from './date-picker';

export {
  Alert,
  App,
  Avatar,
  Badge,
  Breadcrumb,
  Card,
  Checkbox,
  Col,
  Collapse,
  ConfigProvider,
  Descriptions,
  Divider,
  Dropdown,
  Drawer,
  Empty,
  Form,
  Image,
  Input,
  InputNumber,
  Layout,
  Menu,
  Modal,
  Popconfirm,
  Progress,
  Radio,
  Result,
  Row,
  Select,
  Space,
  Spin,
  Statistic,
  Switch,
  Tabs,
  Tag,
  TimePicker,
  Tooltip,
  Typography,
  Upload,
  message,
  theme,
} from 'antd';

export { EmptyState, ErrorState, PageLoadingState, CopyText } from '@wanjianshi/ui-foundation';

function mergeClassName(...values: Array<string | undefined>): string {
  return values.filter(Boolean).join(' ');
}

function renderPaginationTotal(total: number): ReactNode {
  return (
    <span className="wjs-admin-pagination__total">
      共 <strong>{total.toLocaleString('zh-CN')}</strong> 条
    </span>
  );
}

/** 后台统一按钮入口。所有业务按钮共享尺寸、圆角、字重和交互样式。 */
export function Button({ className, ...props }: ButtonProps) {
  return <AntButton className={mergeClassName('wjs-admin-button', className)} {...props} />;
}

export type AdminAction =
  | 'create'
  | 'view'
  | 'edit'
  | 'delete'
  | 'download'
  | 'upload'
  | 'refresh'
  | 'search'
  | 'reset';

const actionIcons: Record<AdminAction, ReactNode> = {
  create: <PlusOutlined />,
  view: <EyeOutlined />,
  edit: <EditOutlined />,
  delete: <DeleteOutlined />,
  download: <DownloadOutlined />,
  upload: <UploadOutlined />,
  refresh: <ReloadOutlined />,
  search: <SearchOutlined />,
  reset: <ReloadOutlined />,
};

export function ActionButton({
  action,
  children,
  className,
  ...props
}: ButtonProps & { action: AdminAction }) {
  const danger = props.danger ?? action === 'delete';
  const type = props.type ?? (action === 'create' || action === 'search' ? 'primary' : 'default');
  return (
    <Button
      {...props}
      danger={danger}
      type={type}
      icon={props.icon ?? actionIcons[action]}
      className={mergeClassName(`wjs-admin-action wjs-admin-action--${action}`, className)}
    >
      {children}
    </Button>
  );
}

export const CreateButton = (props: ButtonProps) => <ActionButton action="create" {...props} />;
export const ViewButton = (props: ButtonProps) => <ActionButton action="view" {...props} />;
export const EditButton = (props: ButtonProps) => <ActionButton action="edit" {...props} />;
export const DeleteButton = (props: ButtonProps) => <ActionButton action="delete" {...props} />;
export const DownloadButton = (props: ButtonProps) => <ActionButton action="download" {...props} />;
export const UploadButton = (props: ButtonProps) => <ActionButton action="upload" {...props} />;
export const RefreshButton = (props: ButtonProps) => <ActionButton action="refresh" {...props} />;
export const SearchButton = (props: ButtonProps) => <ActionButton action="search" {...props} />;
export const ResetButton = (props: ButtonProps) => <ActionButton action="reset" {...props} />;

/** 独立分页器使用统一的总数说明、页容量和布局。 */
export function Pagination({
  className,
  showSizeChanger = true,
  showTotal = renderPaginationTotal,
  pageSizeOptions = [10, 20, 50, 100],
  ...props
}: PaginationProps) {
  return (
    <AntPagination
      {...props}
      className={mergeClassName('wjs-admin-pagination', className)}
      showSizeChanger={showSizeChanger}
      showTotal={showTotal}
      pageSizeOptions={pageSizeOptions}
    />
  );
}

/** 上传区域统一为管理端浅色边框和说明布局。 */
export function UploadDragger({ className, ...props }: UploadProps) {
  return <Upload.Dragger className={mergeClassName('wjs-admin-upload', className)} {...props} />;
}

export function StatusSwitch({
  checkedChildren = '启用',
  unCheckedChildren = '停用',
  ...props
}: SwitchProps) {
  return (
    <Switch
      checkedChildren={checkedChildren}
      unCheckedChildren={unCheckedChildren}
      {...props}
    />
  );
}

export function Toolbar({
  children,
  actions,
}: {
  children?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <div className="wjs-admin-toolbar">
      <div className="wjs-admin-toolbar__filters">{children}</div>
      {actions ? <div className="wjs-admin-toolbar__actions">{actions}</div> : null}
    </div>
  );
}

export function FormActions({ children }: { children: ReactNode }) {
  return <div className="wjs-admin-form-actions">{children}</div>;
}

export function AdminPage({
  title,
  description,
  extra,
  children,
}: {
  title: ReactNode;
  description?: ReactNode;
  extra?: ReactNode;
  children: ReactNode;
}) {
  return (
    <div className="wjs-admin-page">
      <div className="wjs-admin-page__header">
        <div className="wjs-admin-page__heading">
          <Typography.Title level={3}>{title}</Typography.Title>
          {description ? <Typography.Text type="secondary" className="wjs-admin-page__description">{description}</Typography.Text> : null}
        </div>
        {extra ? <div className="wjs-admin-page__extra">{extra}</div> : null}
      </div>
      <div className="wjs-admin-page__body">{children}</div>
    </div>
  );
}

export function SearchPanel({
  form,
  children,
  onSearch,
  onReset,
}: {
  form: FormInstance;
  children: ReactNode;
  onSearch: () => void;
  onReset?: () => void;
}) {
  return (
    <Card size="small" className="wjs-admin-search-panel">
      <Form form={form} layout="inline" onFinish={onSearch} style={{ rowGap: 12 }}>
        {children}
        <Form.Item>
          <Space>
            <SearchButton htmlType="submit">查询</SearchButton>
            <ResetButton onClick={() => { form.resetFields(); onReset?.(); }}>重置</ResetButton>
          </Space>
        </Form.Item>
      </Form>
    </Card>
  );
}

export function Table<T = unknown>({
  className,
  pagination,
  scroll,
  size = 'middle',
  ...props
}: Omit<TableProps<any>, 'dataSource'> & { dataSource?: readonly T[] }) {
  const normalizedPagination: TablePaginationConfig | false = pagination === false
    ? false
    : {
        showSizeChanger: true,
        showTotal: renderPaginationTotal,
        pageSizeOptions: [10, 20, 50, 100],
        placement: ['bottomEnd'],
        ...pagination,
      };
  return (
    <AntTable<any>
      {...props as TableProps<any>}
      dataSource={props.dataSource as readonly any[] | undefined}
      className={mergeClassName(
        'wjs-admin-table',
        normalizedPagination === false ? 'wjs-admin-table--without-pagination' : 'wjs-admin-table--paginated',
        props.rowSelection ? 'wjs-admin-table--selectable' : undefined,
        className,
      )}
      size={size}
      pagination={normalizedPagination}
      scroll={scroll ?? { x: 'max-content' }}
      locale={{
        emptyText: (
          <AntEmpty
            className="wjs-admin-table__empty"
            image={AntEmpty.PRESENTED_IMAGE_SIMPLE}
            description="暂无数据"
          />
        ),
        ...props.locale,
      }}
    />
  );
}

export function DataTable<T = unknown>(props: Omit<TableProps<any>, 'dataSource'> & { dataSource?: readonly T[] }) {
  return <Table<T> {...props} />;
}

export function DetailDrawer({
  open,
  title,
  onClose,
  children,
  width = 760,
}: {
  open: boolean;
  title: ReactNode;
  onClose: () => void;
  children: ReactNode;
  width?: number;
}) {
  return <Drawer open={open} title={title} onClose={onClose} size={width}>{children}</Drawer>;
}

export function DetailDescriptions(props: DescriptionsProps) {
  return <Descriptions bordered size="small" column={2} {...props} />;
}

export function StatusTag({ value, label, color }: { value?: string | number | null; label?: ReactNode; color?: string }) {
  return <Tag color={resolveStatusColor(value, color)}>{label ?? resolveStatusLabel(value)}</Tag>;
}

export function PermissionButton({
  allowed = true,
  children,
  ...buttonProps
}: ButtonProps & { allowed?: boolean; children: ReactNode }) {
  if (!allowed) return null;
  return <Button {...buttonProps}>{children}</Button>;
}

export function SensitiveText({ value, masked = true }: { value?: string | null; masked?: boolean }) {
  return <Typography.Text copyable={!masked && value ? { text: value } : false}>{value || '-'}</Typography.Text>;
}

export function ResourceLink({ href, children }: { href: string; children: ReactNode }) {
  return <Typography.Link href={href}>{children}</Typography.Link>;
}

export function AuditTimeline({
  items,
}: {
  items: Array<{ key: string | number; title: ReactNode; detail?: ReactNode; time?: ReactNode; color?: string }>;
}) {
  return (
    <Timeline
      items={items.map(item => ({
        color: item.color,
        children: (
          <Space direction="vertical" size={0}>
            <Typography.Text strong>{item.title}</Typography.Text>
            {item.detail ? <Typography.Text>{item.detail}</Typography.Text> : null}
            {item.time ? <Typography.Text type="secondary">{item.time}</Typography.Text> : null}
          </Space>
        ),
      }))}
    />
  );
}

export function RiskActionButton({
  allowed = true,
  title,
  description,
  reasonRequired = true,
  danger = true,
  onConfirm,
  children,
  ...buttonProps
}: Omit<ButtonProps, 'onClick'> & {
  allowed?: boolean;
  title: ReactNode;
  description?: ReactNode;
  reasonRequired?: boolean;
  onConfirm: (reason: string) => Promise<void> | void;
}) {
  const [modal, contextHolder] = Modal.useModal();
  const execute = () => {
    let reason = '';
    modal.confirm({
      title,
      content: (
        <Space direction="vertical" style={{ width: '100%' }}>
          {description}
          {reasonRequired ? (
            <Input.TextArea
              autoSize={{ minRows: 3, maxRows: 6 }}
              placeholder="请输入操作原因"
              onChange={event => { reason = normalizeRiskReason(event.target.value); }}
            />
          ) : null}
        </Space>
      ),
      okText: '确认执行',
      okButtonProps: { danger },
      async onOk() {
        if (reasonRequired && !reason) throw new Error('请输入操作原因');
        await onConfirm(reason);
      },
    });
  };
  if (!allowed) return null;
  return (
    <>
      {contextHolder}
      <Button
        danger={danger}
        {...buttonProps}
        className={mergeClassName('wjs-admin-action wjs-admin-action--delete', buttonProps.className)}
        onClick={execute}
      >
        {children}
      </Button>
    </>
  );
}
